import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Clock, ChevronRight, PlayCircle } from 'lucide-react';
import AudioPlayer from './ui/AudioPlayer';
import QuizQuestionCard from './QuizQuestionCard';
import QuizSubmissionDetailsModal from './UserQuizSubmissionDetail';
import { useApiCaller } from '../utils/ApiUtilities';
import { getCustomQuizGetByIdUrl, getGrePerformanceUrl } from '../utils/UrlUtilities';
import { OPTIONFIELDTOCORRECTOPTIONMAP } from '../utils/Constants';
import { loadInProgressAnswers, saveInProgressAnswers, clearInProgressAnswers, loadToeflProgress, saveToeflProgress, clearToeflProgress } from '../utils/BasicUtilities';
import useAnswerAutosave from '../utils/useAnswerAutosave';
import AnsweredTracker from './ui/AnsweredTracker';
import HighlightableText from './ui/HighlightableText';
import { formatBdPst } from '../utils/DateUtilities';

// ---------------------------------------------------------------------------
// GRE model-test taker. Renders a Quiz whose metadata.test_kind === 'gre_model_test'
// as the real exam: ordered sections (Analytical Writing → Verbal → Quant), each its own
// separately-timed module you cannot return to once you move on. Handles the GRE item types:
//   MCQ single           -> selected_option "A"
//   Sentence Equivalence -> selected_option "A,C"  (pick exactly two)
//   Text Completion      -> selected_option "1,0,2" (ordered per-blank choice indices)
//   Essay (TEXT)         -> text_answer "..."       (free-write, not auto-scored)
// Each sub-question is an ordinary 1-point question on the server, so scoring / statistics /
// leaderboards work unchanged; this only assembles + submits answers keyed by question_id.
// Competitions (also test_kind gre_model_test) reuse this taker; their results unlock only
// after the competition window closes.
// ---------------------------------------------------------------------------

const DEFAULT_MODULE_MINUTES = 20;
const NAVBAR_OFFSET = '74px';

const fmt = (secs) => {
    const s = Math.max(0, secs | 0);
    const m = Math.floor(s / 60);
    return `${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};

// Per-section sequential question numbers, restarting at 1 each section (blueprint order).
function buildNumberByQid(blueprint) {
    const map = {};
    (blueprint?.sections || []).forEach((sec) => {
        let n = 1;
        (sec.modules || []).forEach((m) => {
            (m.tasks || []).forEach((t) => {
                (t.question_ids || []).forEach((id) => { map[id] = n; n += 1; });
            });
        });
    });
    return map;
}

// Convert the in-progress answer map into the [{question_id, selected_option|text_answer}] list
// the submission endpoint expects (matches ToeflModelTest.serializeAnswers, plus Text Completion).
function serializeAnswers(questionsById, answers) {
    const out = [];
    Object.keys(answers).forEach((qid) => {
        const q = questionsById[qid];
        const val = answers[qid];
        const entry = { question_id: Number(qid) };
        const qtype = q?.question_type;
        if (qtype === 'TEXT') {
            entry.text_answer = (val && val.text) || '';
        } else if (qtype === 'TEXT_COMPLETION') {
            // { blanks: { blankIdx: choiceIdx } } -> "c0,c1,c2" in blank order.
            const blanks = (val && val.blanks) || {};
            const idxs = Object.keys(blanks).map(Number).sort((a, b) => a - b);
            entry.selected_option = idxs.map((i) => blanks[i]).join(',');
        } else if (Array.isArray(val)) {
            entry.selected_option = val.map((k) => OPTIONFIELDTOCORRECTOPTIONMAP[k]).filter(Boolean).sort().join(',');
        } else if (typeof val === 'string') {
            entry.selected_option = OPTIONFIELDTOCORRECTOPTIONMAP[val] || '';
        }
        out.push(entry);
    });
    return out;
}

export default function GreModelTest({ quizParam, onQuizEnd }) {
    const CallApi = useApiCaller();
    const submissionId = quizParam?.submission_id;
    const savedPos = loadToeflProgress(submissionId);
    const [questionsById, setQuestionsById] = useState({});
    const [blueprint, setBlueprint] = useState(null);
    const [answers, setAnswers] = useState(() => loadInProgressAnswers(submissionId));
    const [moduleIdx, setModuleIdx] = useState(savedPos?.moduleIdx || 0);
    const [started, setStarted] = useState(!!savedPos?.started);
    const [loading, setLoading] = useState(true);
    const [ended, setEnded] = useState(false);
    const [confirmLeave, setConfirmLeave] = useState(false);
    const [confirmQuit, setConfirmQuit] = useState(false);
    const [showSubmissionDetails, setShowSubmissionDetails] = useState(false);
    const [highlightsByPassage, setHighlightsByPassage] = useState({});
    // Per-module scaled scores (each 0–20), fetched once the test ends (unless results are locked).
    const [perf, setPerf] = useState(null);
    // Competition results stay locked until the competition window (quiz.end_time) closes.
    const [resultsLocked, setResultsLocked] = useState(false);
    const [windowEnd, setWindowEnd] = useState(null);
    const submittedRef = useRef(false);

    // Each GRE section carries a single module; flatten to an ordered module list.
    const modules = useMemo(() => {
        const out = [];
        (blueprint?.sections || []).forEach((sec) => {
            (sec.modules || []).forEach((m, mi) => {
                out.push({ sectionName: sec.name, sectionKey: sec.key, nav: sec.navigation, sectionModuleCount: (sec.modules || []).length, moduleNo: mi + 1, ...m });
            });
        });
        return out;
    }, [blueprint]);

    const numberByQid = useMemo(() => buildNumberByQid(blueprint), [blueprint]);

    useEffect(() => {
        (async () => {
            try {
                const res = await CallApi({ url: getCustomQuizGetByIdUrl(quizParam.quiz_id), method: 'GET', token: true });
                if (res.success) {
                    const byId = {};
                    Object.values(res.data.questions || {}).forEach((arr) => arr.forEach((q) => { byId[q.question_id] = q; }));
                    setQuestionsById(byId);
                    setBlueprint(res.data.metadata || { sections: [] });
                    const isComp = res.data.quiz_type === 'compitition';
                    const et = res.data.end_time;
                    setResultsLocked(isComp && (!et || new Date(et) > new Date()));
                    setWindowEnd(isComp ? et : null);
                }
            } finally {
                setLoading(false);
            }
        })();
    }, [CallApi, quizParam.quiz_id]);

    const setText = useCallback((qid, text) => setAnswers((a) => ({ ...a, [qid]: { text } })), []);
    const setOption = useCallback((qid, key) => setAnswers((a) => ({ ...a, [qid]: key })), []);
    const toggleMulti = useCallback((qid, key) => setAnswers((a) => {
        const cur = Array.isArray(a[qid]) ? a[qid] : [];
        return { ...a, [qid]: cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key] };
    }), []);
    // Text Completion: one choice per blank, stored as { blanks: { blankIdx: choiceIdx } }.
    const setBlank = useCallback((qid, blankIdx, choiceIdx) => setAnswers((a) => {
        const cur = (a[qid] && a[qid].blanks) || {};
        const next = { ...cur };
        if (next[blankIdx] === choiceIdx) delete next[blankIdx];
        else next[blankIdx] = choiceIdx;
        if (Object.keys(next).length === 0) {
            const { [qid]: _drop, ...rest } = a;
            return rest;
        }
        return { ...a, [qid]: { blanks: next } };
    }), []);

    useEffect(() => { saveInProgressAnswers(submissionId, answers); }, [submissionId, answers]);
    // When the test ends (and results aren't competition-locked), pull the per-module scaled scores.
    useEffect(() => {
        if (!ended || resultsLocked || !submissionId) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const res = await CallApi({ url: getGrePerformanceUrl(submissionId), method: 'GET', token: true });
                if (!cancelled && res.success) setPerf(res.data);
            } catch (e) { /* scores are optional */ }
        })();
        return () => { cancelled = true; };
    }, [ended, resultsLocked, submissionId, CallApi]);
    useEffect(() => { saveToeflProgress(submissionId, { moduleIdx, started }); }, [submissionId, moduleIdx, started]);
    // Server backstop: debounce-save answers so an abandoned attempt (browser closed before the
    // timer fires) can still be finalized+scored by the server's overdue sweep.
    const { scheduleSave, flush } = useAnswerAutosave(submissionId);
    useEffect(() => {
        if (submittedRef.current || Object.keys(answers).length === 0) return;
        scheduleSave(serializeAnswers(questionsById, answers));
    }, [answers, questionsById, scheduleSave]);

    const submit = useCallback(() => {
        if (submittedRef.current) return;
        submittedRef.current = true;
        setEnded(true);
        clearInProgressAnswers(submissionId);
        clearToeflProgress(submissionId);
        Promise.resolve(onQuizEnd({ ...quizParam, submitted_answers: serializeAnswers(questionsById, answers) }));
    }, [answers, questionsById, onQuizEnd, quizParam, submissionId]);

    const current = modules[moduleIdx];
    const isLast = moduleIdx >= modules.length - 1;

    const goNext = useCallback(() => {
        flush(); // push the latest answers to the server before leaving this section
        if (isLast) submit();
        else { setModuleIdx((i) => i + 1); setStarted(false); }
    }, [isLast, submit, flush]);

    // The countdown effect below intentionally does NOT depend on goNext (so a keystroke doesn't
    // reset the timer). Route expiry through a ref that always points at the latest goNext, so it
    // submits the answers the user actually entered instead of a stale (often empty) snapshot.
    const goNextRef = useRef(goNext);
    useEffect(() => { goNextRef.current = goNext; });

    // Per-module countdown — arms when the module begins; auto-advances on expiry.
    const [secsLeft, setSecsLeft] = useState(0);
    useEffect(() => {
        if (!current || !started) return undefined;
        setSecsLeft((current.duration_minutes || DEFAULT_MODULE_MINUTES) * 60);
        const t = setInterval(() => {
            setSecsLeft((s) => {
                if (s <= 1) { clearInterval(t); goNextRef.current(); return 0; }
                return s - 1;
            });
        }, 1000);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [moduleIdx, started, loading]);

    const moduleQuestionCount = (m) => (m.tasks || []).reduce((n, t) => n + (t.question_ids || []).length, 0);

    // Cluster a task's questions by shared group_id so a reading passage renders once above its
    // questions; standalone TC/SE/MCQ render as their own cards.
    const renderQuestions = (qs) => {
        const clusters = [];
        qs.forEach((q) => {
            const last = clusters[clusters.length - 1];
            if (q.group_id && last && last.gid === q.group_id) last.items.push(q);
            else clusters.push({ gid: q.group_id || null, items: [q] });
        });
        return clusters.map((c, ci) => {
            if (!c.gid) {
                return c.items.map((q, i) => (
                    <QuizQuestionCard key={q.question_id} question={q} qIndex={i} displayNumber={numberByQid[q.question_id]}
                        textareaRows={q.question_type === 'TEXT' ? 12 : 3} includePassage
                        answer={answers[q.question_id]} onOptionSelect={setOption} onMultiToggle={toggleMulti}
                        onTextChange={setText} onBlankSelect={setBlank} />
                ));
            }
            const head = c.items[0];
            return (
                <div key={`g-${c.gid}-${ci}`} style={{ marginBottom: '1rem', border: '1px solid #e9d5ff', borderRadius: '0.5rem', overflow: 'hidden' }}>
                    {(head.side_text || head.audio_url) && (
                        <div style={{ background: '#f5f3ff', padding: '0.75rem 1rem', borderBottom: '1px solid #e9d5ff' }}>
                            {head.side_text && <HighlightableText className="kp-read-passage" style={{ marginBottom: head.audio_url ? '0.5rem' : 0 }} text={head.side_text} highlights={highlightsByPassage[c.gid] || []} onChange={(next) => setHighlightsByPassage(prev => ({ ...prev, [c.gid]: next }))} />}
                            {head.audio_url && <AudioPlayer src={head.audio_url} />}
                        </div>
                    )}
                    <div style={{ padding: '0.75rem 1rem' }}>
                        {c.items.map((q, i) => (
                            <QuizQuestionCard key={q.question_id} question={q} qIndex={i} displayNumber={numberByQid[q.question_id]} includePassage={false}
                                answer={answers[q.question_id]} onOptionSelect={setOption} onMultiToggle={toggleMulti}
                                onTextChange={setText} onBlankSelect={setBlank} />
                        ))}
                    </div>
                </div>
            );
        });
    };

    const renderTask = (task) => {
        const qs = (task.question_ids || []).map((id) => questionsById[id]).filter(Boolean);
        return renderQuestions(qs);
    };

    if (loading) return <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }}><div className="modal-dialog modal-dialog-centered"><div className="modal-content p-4 text-center">Loading model test…</div></div></div>;

    if (ended) {
        return (
            <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content p-4">
                        <h4 className="fw-bold mb-2 text-center">Test submitted ✅</h4>
                        <p className="text-muted text-center">
                            Verbal &amp; Quant are auto-scored; the Analytical Writing essay is recorded but not auto-scored.
                        </p>
                        {perf && !resultsLocked && (() => {
                            // Full published test (4 scored modules) shows the /340 total; a topic-wise
                            // (single-module) test shows just that module's raw + /20 scale, no total.
                            const isFull = perf.scored_count >= 4;
                            return (
                                <div className="mb-3">
                                    <table className="table table-sm align-middle mb-1">
                                        <thead><tr><th>Section</th><th className="text-center">Correct</th><th className="text-center">Scaled / 20</th></tr></thead>
                                        <tbody>
                                            {(perf.sections || []).map((s, i) => (
                                                <tr key={i}>
                                                    <td>{s.name}</td>
                                                    <td className="text-center">{s.unscored ? '—' : `${s.raw}/${s.max}`}</td>
                                                    <td className="text-center">{s.unscored ? <span className="text-muted">Not scored</span> : s.scaled}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        {isFull && (
                                            <tfoot>
                                                <tr className="fw-bold">
                                                    <td colSpan={2}>Total (260 base + 4×20)</td>
                                                    <td className="text-center">{perf.total_scaled} / 340</td>
                                                </tr>
                                            </tfoot>
                                        )}
                                    </table>
                                    <div className="text-muted small text-center">
                                        {isFull
                                            ? 'Each scored module is scaled to 20; a full test adds the four to a 260 base (max 340).'
                                            : 'This module is scaled to 20 (correct ÷ scoreable).'}
                                    </div>
                                </div>
                            );
                        })()}
                        {resultsLocked ? (
                            <p className="text-muted small text-center mb-2">
                                Your answers are submitted. Full submission details &amp; the leaderboard open
                                after the competition ends{windowEnd ? <> — <strong>{formatBdPst(windowEnd)}</strong></> : null}.
                            </p>
                        ) : (
                            <button className="btn btn-primary w-100 mb-2" onClick={() => setShowSubmissionDetails(true)}>Show Submission Details</button>
                        )}
                        <button className="btn btn-primary w-100" onClick={() => onQuizEnd(null)}>Close</button>
                    </div>
                </div>
                {showSubmissionDetails && (
                    <QuizSubmissionDetailsModal submissionId={submissionId} onClose={() => setShowSubmissionDetails(false)} />
                )}
            </div>
        );
    }
    if (!current) return null;

    // Instruction / Begin screen before each module. No timer runs until the user clicks Begin.
    if (!started) {
        const firstOfSection = moduleIdx === 0 || modules[moduleIdx - 1].sectionKey !== current.sectionKey;
        const sectionModules = modules.filter((m) => m.sectionKey === current.sectionKey);
        const sectionQs = sectionModules.reduce((n, m) => n + moduleQuestionCount(m), 0);
        return (
            <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.6)' }} tabIndex="-1">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content p-4">
                        {firstOfSection && (
                            <div className="alert alert-primary">
                                <div className="fw-bold fs-5 mb-1">{current.sectionName}</div>
                                <div>{sectionQs} question{sectionQs > 1 ? 's' : ''} total.</div>
                                <div className="small mt-1">You cannot return to this section once you move on. Each section is separately timed.</div>
                            </div>
                        )}
                        <h5 className="fw-bold text-dark">{current.sectionName}</h5>
                        <ul className="text-muted">
                            <li>{moduleQuestionCount(current)} question{moduleQuestionCount(current) > 1 ? 's' : ''}</li>
                            <li>Time: {current.duration_minutes || DEFAULT_MODULE_MINUTES} minute{(current.duration_minutes || DEFAULT_MODULE_MINUTES) > 1 ? 's' : ''} (the clock starts when you begin)</li>
                        </ul>
                        <button className="btn btn-primary w-100 mt-2" onClick={() => setStarted(true)}>
                            <PlayCircle size={18} style={{ marginRight: 6 }} /> Begin {current.name}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const moduleQuestions = (current.tasks || []).flatMap((t) => (t.question_ids || []).map((id) => questionsById[id])).filter(Boolean);
    const isAnswered = (q) => {
        const v = answers[q.question_id];
        if (v == null) return false;
        if (typeof v === 'string') return v.trim() !== '';
        if (Array.isArray(v)) return v.length > 0;
        if (typeof v === 'object') {
            if (v.blanks) return Object.keys(v.blanks).length > 0; // Text Completion
            return !!(v.text && v.text.trim());                    // Essay
        }
        return false;
    };

    return (
        <>
        <div style={{ position: 'fixed', top: NAVBAR_OFFSET, left: 0, right: 0, bottom: 0, zIndex: 900, display: 'flex', flexDirection: 'column', background: '#f8fafc' }}>
            <div className="d-flex justify-content-between align-items-center" style={{ background: 'linear-gradient(145deg,#6d28d9,#4338ca)', color: '#fff', padding: '0.75rem 1.25rem', flexShrink: 0, boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
                <div>
                    <div className="fw-bold fs-5">{current.sectionName}</div>
                    <div className="small" style={{ opacity: 0.85 }}>Section {moduleIdx + 1} of {modules.length}</div>
                </div>
                <div className={`d-flex align-items-center gap-2 fs-5 fw-bold${secsLeft <= 60 ? ' kp-timer-flash' : ''}`} style={{ backgroundColor: '#ef4444', color: '#ffffff', padding: '0.3rem 0.8rem', borderRadius: '0.5rem' }} title="Time left in this section">
                    <Clock size={18} /> <span className="small fw-normal">Time left</span> {fmt(secsLeft)}
                </div>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '1.25rem' }}>
                {(current.tasks || []).map((task, ti) => (
                    <div key={ti} style={{ marginBottom: '1.5rem' }}>
                        {task.title && <h5 className="fw-bold text-dark mb-1">{task.title}</h5>}
                        {task.instructions && <p className="text-muted">{task.instructions}</p>}
                        {renderTask(task)}
                    </div>
                ))}
            </div>
            {moduleQuestions.length > 0 && (
                <AnsweredTracker questions={moduleQuestions} isAnswered={isAnswered} numberByQid={numberByQid} style={{ flexShrink: 0 }} />
            )}
            <div className="d-flex justify-content-between" style={{ padding: '0.75rem 1.25rem', borderTop: '1px solid #e5e7eb', background: '#fff', flexShrink: 0 }}>
                <button className="btn btn-outline-secondary" onClick={() => setConfirmQuit(true)}>Quit &amp; submit</button>
                <button className="btn btn-primary" onClick={() => setConfirmLeave(true)}>
                    {isLast ? 'Finish test' : 'Next section'} <ChevronRight size={16} />
                </button>
            </div>
        </div>
        {confirmLeave && (
            <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.7)', zIndex: 1060 }} tabIndex="-1">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content p-4 text-center">
                        <div style={{ fontSize: '2rem' }}>⚠️</div>
                        <h5 className="fw-bold mb-2">This closes {current.sectionName}</h5>
                        <p className="text-muted mb-3">
                            You will <strong>not be able to return</strong> to {current.sectionName} once you continue.
                            Please make sure you have answered everything you want to.
                        </p>
                        <div className="d-flex gap-2 justify-content-center">
                            <button className="btn btn-outline-secondary" onClick={() => setConfirmLeave(false)}>Stay on this section</button>
                            <button className="btn btn-primary" onClick={() => { setConfirmLeave(false); goNext(); }}>
                                {isLast ? 'Finish test' : 'Continue to next section'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )}
        {confirmQuit && (
            <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.7)', zIndex: 1060 }} tabIndex="-1">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content p-4 text-center">
                        <div style={{ fontSize: '2rem' }}>🚪</div>
                        <h5 className="fw-bold mb-2">Quit the test?</h5>
                        <p className="text-muted mb-3">
                            Your progress will be <strong>submitted as-is</strong> and the test will end.
                            Unanswered questions stay blank. This can't be undone.
                        </p>
                        <div className="d-flex gap-2 justify-content-center">
                            <button className="btn btn-outline-secondary" onClick={() => setConfirmQuit(false)}>Keep taking the test</button>
                            <button className="btn btn-danger" onClick={() => { setConfirmQuit(false); submit(); }}>Quit &amp; submit</button>
                        </div>
                    </div>
                </div>
            </div>
        )}
        </>
    );
}
